import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Tooltip,
  ListItem,
  ListItemText,
  List,
  Grid,
  Fade,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Medication as MedicationIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { searchMedications, getMedicationDetails } from '../../../api/medications';
import { LocalPharmacyIcon } from '../../../icons';
import { alpha } from '@mui/material/styles';
import { color, r } from '../../../theme/tokens';

const FREQUENCY_OPTIONS = [
  { code: 'QD', text: 'Once daily' },
  { code: 'BID', text: 'Twice daily' },
  { code: 'TID', text: 'Three times daily' },
  { code: 'QID', text: 'Four times daily' },
  { code: 'QOD', text: 'Every other day' },
  { code: 'QWK', text: 'Once weekly' },
  { code: 'PRN', text: 'As needed' }
];

// Centralized function to create a medication object.
// This ensures a consistent data structure.
const createMedicationObject = (details, strengthInfo, frequencyInfo, selectedMedication) => {
  // Parse strength value and unit
  const strengthDetails = parseStrengthValue(strengthInfo.strengthString);
  
  // Get frequency details
  const frequencyData = FREQUENCY_OPTIONS.find(f => f.code === frequencyInfo.frequencyCode);

  return {
    name: {
      display: details.display_name || selectedMedication.display_name,
      generic: details.generic_name,
      brand: details.brand_name,
    },
    is_brand: details.is_brand || false,
    dosage: {
      strength: strengthDetails.value,
      unit: strengthDetails.unit,
      form: details.doseForm || 'N/A',
      route: details.route || 'N/A',
    },
    frequency: {
      code: frequencyInfo.frequencyCode,
      text: frequencyData?.text || frequencyInfo.frequencyCode,
    },
    identifiers: {
      rxcui: details.rxcui,
      tty: details.tty,
      in_rxcui: details.in_rxcui, // Phase F: Ingredient-level RxCUI for ID-based correlation
      ingredient_name: details.ingredient_name, // Phase F.1: Ingredient name for AI analysis
    },
    raw: {
      rxnorm_name: details.name, // The original, unaltered name from the API
      strength_string: strengthInfo.strengthString,
    },
    // Generate a unique ID for frontend-only use (e.g., React keys)
    id: `${details.rxcui}_${strengthInfo.strengthString}_${Date.now()}`,
  };
};

// Helper to parse strength string into value and unit
const parseStrengthValue = (strengthString) => {
  if (!strengthString || strengthString === 'N/A') {
    return { value: null, unit: null };
  }

  // Common patterns for strength parsing
  const patterns = [
    /^(\d+(?:\.\d+)?)\s*(mg|g|mcg|µg|units?|iu|mL|L)\b/i,
    /^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)/,
    /^(\d+(?:\.\d+)?)/
  ];

  for (const pattern of patterns) {
    const match = strengthString.match(pattern);
    if (match) {
      return {
        value: parseFloat(match[1]),
        unit: match[2] ? match[2].toUpperCase() : null
      };
    }
  }

  return { value: null, unit: strengthString };
};


function MedicationList({ medications = [], onChange }) {
  // Search and selection states
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form states for adding medication
  const [selectedStrength, setSelectedStrength] = useState('');
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // UI states
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  const searchInputRef = useRef(null);
  const addFormRef = useRef(null);

  // Auto-hide success message with fade animation
  useEffect(() => {
    if (success) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setTimeout(() => setSuccess(''), 300); // Clear after fade animation
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Ensure add form is visible on mobile when opened
  useEffect(() => {
    if (showAddForm && addFormRef.current) {
      setTimeout(() => {
        try {
          addFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {
          // no-op
        }
      }, 100);
    }
  }, [showAddForm]);

  // Search for medications with debouncing
  const handleSearchChange = async (event, newValue, reason) => {
    if (reason === 'input') {
      setSearchQuery(newValue);
      setError('');
      if (success) {
        setShowSuccess(false);
        setTimeout(() => setSuccess(''), 300);
      }
      
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      if (newValue.length < 2) {
        setSuggestions([]);
        return;
      }
      
      // Debounce the search
      const timeout = setTimeout(async () => {
        setLoading(true);
        try {
          const results = await searchMedications(newValue);
          setSuggestions(results || []);
        } catch (error) {
          console.error('Search error:', error);
          setError('Failed to search medications. Please try again.');
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, 300);
      
      setSearchTimeout(timeout);
    }
  };

  // Handle selecting a medication from search results
  const handleMedicationSelect = (event, value) => {
    if (value) {
      setSelectedMedication(value);
      setShowAddForm(true);
      setSelectedStrength('');
      setSelectedFrequency('');
      setSuggestions([]);
      // Reflect the full, human-readable name in the search bar after selection
      const fullName = value.full_name || value.display_name || value.name || '';
      setSearchQuery(fullName);
    }
  };

  // Add medication to the list
  const handleAddMedication = async () => {
    if (!selectedMedication) {
      setError('Please select a medication.');
      return;
    }

    if (selectedMedication.strengths?.length > 0 && !selectedStrength) {
      setError('Please select a dosage.');
      return;
    }

    setLoading(true);
    setError('');
    if (success) {
      setShowSuccess(false);
      setTimeout(() => setSuccess(''), 300);
    }

    try {
      // Get the RxCUI for the selected strength or main medication
      let rxcui = null;
      let strengthString = 'N/A';
      
      if (selectedMedication.strengths?.length > 0 && selectedStrength) {
        const strengthIndex = parseInt(selectedStrength);
        rxcui = selectedMedication.rxcuis?.[strengthIndex];
        strengthString = selectedMedication.strengths[strengthIndex];
      } else {
        rxcui = selectedMedication.sxdg_rxcui || selectedMedication.rxcuis?.[0];
      }

      if (!rxcui) {
        setError('Could not find a valid identifier for this medication.');
        return;
      }

      // Fetch detailed medication information
      const details = await getMedicationDetails(rxcui);
      
      const newMedication = createMedicationObject(
        details,
        { strengthString },
        { frequencyCode: selectedFrequency || 'QD' },
        selectedMedication
      );

      // Check for duplicates using the new structure
      const isDuplicate = medications.some(med => 
        med.identifiers?.rxcui === newMedication.identifiers.rxcui &&
        med.raw?.strength_string === newMedication.raw.strength_string
      );

      if (isDuplicate) {
        setError('This medication with the same strength is already in your list.');
        return;
      }

      // Add to medications list
      const updatedMedications = [...medications, newMedication];
      onChange(updatedMedications);

      // Reset form - no success message since it's not saved yet
      setSelectedMedication(null);
      setShowAddForm(false);
      setSearchQuery('');
      setSelectedStrength('');
      setSelectedFrequency('');

    } catch (error) {
      console.error('Error adding medication:', error);
      setError('Failed to add medication. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Remove medication from the list
  const handleRemoveMedication = (medicationId) => {
    const updatedMedications = medications.filter(med => med.id !== medicationId);
    onChange(updatedMedications);
  };

  // The parseStrengthValue function is now a standalone helper outside the component
  
  // Format medication display name - no longer needed as a complex function,
  // the new data structure is already clean.

  // Cancel adding medication
  const handleCancelAdd = () => {
    setSelectedMedication(null);
    setShowAddForm(false);
    setSelectedStrength('');
    setSelectedFrequency('');
    setError('');
  };

  // Handle updating medication (for frequency editing)
  const handleUpdateMedication = (updatedMedication) => {
    const updatedMedications = medications.map(med => 
      med.id === updatedMedication.id ? updatedMedication : med
    );
    onChange(updatedMedications);
  };

  // Add CompactMedicationCard component inside the main function
  const CompactMedicationCard = ({ medication, onUpdate, onRemove }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [tempValues, setTempValues] = useState({
      frequency: medication.frequency
    });

    const handleEdit = (field) => {
      if (field === 'frequency') { // Only allow frequency editing
        setEditingField(field);
        setIsEditing(true);
      }
    };

    const handleSave = () => {
      const updatedMedication = {
        ...medication,
        frequency: tempValues.frequency
      };
      onUpdate(updatedMedication);
      setIsEditing(false);
      setEditingField(null);
    };

    const handleCancel = () => {
      setTempValues({
        frequency: medication.frequency
      });
      setIsEditing(false);
      setEditingField(null);
    };

    const handleFrequencyChange = (e) => {
      const selectedFreq = FREQUENCY_OPTIONS.find(f => f.code === e.target.value);
      if (selectedFreq) {
        setTempValues(prev => ({
          ...prev,
          frequency: {
            code: selectedFreq.code,
            text: selectedFreq.text
          }
        }));
      }
    };

    const strengthDisplay = medication.dosage?.strength
      ? `${medication.dosage.strength} ${medication.dosage.unit || ''}`.trim()
      : medication.raw?.strength_string || 'Not specified';

    return (
      <Card 
        elevation={0}
        sx={{ 
          border: `${r(1)} solid ${alpha(color.accent, 0.2)}`,
          borderRadius: 2,
          transition: 'all 0.2s ease',
          '&:hover': { 
            boxShadow: `0 ${r(2)} ${r(8)} ${alpha(color.accent, 0.15)}`,
            borderColor: alpha(color.accent, 0.3)
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1 }}>
              {/* Medication Name - Simplified with new data structure */}
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{
                      color: color.evergreen,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {medication.name.display}
                    {medication.is_brand && medication.name.generic && (
                      <Typography 
                        component="span" 
                        variant="body2" 
                        sx={{ 
                          color: color.inkSoft,
                          fontWeight: 500,
                          ml: 1,
                          fontSize: '0.9rem'
                        }}
                      >
                        ({medication.name.generic})
                      </Typography>
                    )}
                  </Typography>
                </Box>
                
                {medication.raw?.rxnorm_name && medication.raw.rxnorm_name !== medication.name.display && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary', 
                      fontSize: '0.85rem',
                      fontStyle: 'italic',
                      lineHeight: 1.3,
                      mt: 0.25
                    }}
                  >
                    {medication.raw.rxnorm_name}
                  </Typography>
                )}
              </Box>
              
              {/* Compact Details Row */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1.5, 
                alignItems: 'center',
                mb: 1
              }}>
                {/* Strength */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Strength:
                  </Typography>
                  {/* Removed strength editing */}
                  <Chip 
                    label={strengthDisplay}
                    size="small"
                    sx={{
                      height: r(22),
                      fontSize: '0.7rem',
                      backgroundColor: alpha(color.accent, 0.15),
                      color: color.evergreen,
                      border: `${r(1)} solid ${alpha(color.accent, 0.3)}`, // Solid border since not editable
                    }}
                  />
                </Box>

                {/* Frequency */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Frequency:
                  </Typography>
                  {isEditing && editingField === 'frequency' ? (
                    <FormControl size="small">
                      <Select
                        value={tempValues.frequency.code}
                        onChange={handleFrequencyChange}
                        autoFocus
                        sx={{
                          height: r(24),
                          fontSize: '0.75rem',
                          '& .MuiSelect-select': { py: 0.25 }
                        }}
                      >
                        {FREQUENCY_OPTIONS.map((freq) => (
                          <MenuItem key={freq.code} value={freq.code} sx={{ fontSize: '0.75rem' }}>
                            {freq.text}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Tooltip title="Click to edit frequency" placement="top">
                      <Chip 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{medication.frequency.text}</span>
                            <EditIcon sx={{ fontSize: '0.7rem', opacity: 0.7 }} />
                          </Box>
                        }
                        size="small"
                        onClick={() => handleEdit('frequency')}
                        sx={{
                          height: r(22),
                          fontSize: '0.7rem',
                          backgroundColor: alpha(color.evergreen, 0.1),
                          color: color.evergreen,
                          cursor: 'pointer',
                          border: `${r(1)} dashed ${alpha(color.evergreen, 0.2)}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: alpha(color.evergreen, 0.2),
                            borderColor: alpha(color.evergreen, 0.4),
                            transform: 'scale(1.02)',
                            '& .MuiSvgIcon-root': {
                              opacity: 1,
                            }
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>

                {/* Form & Route */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {medication.dosage.form} • {medication.dosage.route}
                  </Typography>
                </Box>
              </Box>

              {/* Edit Actions */}
              {isEditing && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Tooltip title="Save changes" placement="top">
                    <Button 
                      size="small" 
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        minWidth: r(32),
                        width: r(32),
                        height: r(28),
                        p: 0,
                        background: color.accent,
                        '&:hover': {
                          background: color.accentHover,
                        }
                      }}
                    >
                      <CheckIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                  </Tooltip>
                  <Tooltip title="Cancel editing" placement="top">
                    <Button 
                      size="small" 
                      onClick={handleCancel}
                      variant="outlined"
                      sx={{
                        minWidth: r(32),
                        width: r(32),
                        height: r(28),
                        p: 0,
                        borderColor: alpha(color.accent, 0.5),
                        color: color.evergreen,
                        '&:hover': {
                          borderColor: color.accent,
                          backgroundColor: alpha(color.accent, 0.04),
                        }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '1rem' }} />
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>
            
            {/* Remove Button */}
            <IconButton 
              onClick={onRemove}
              size="small"
              sx={{
                color: color.destructive,
                opacity: 0.7,
                '&:hover': {
                  backgroundColor: alpha(color.destructive, 0.04),
                  opacity: 1,
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      {/* Search Section - Fixed spacing and removed duplicate icon */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2,
          mb: 2,
          background: `linear-gradient(135deg, ${alpha(color.accent, 0.08)} 0%, ${alpha(color.evergreen, 0.03)} 100%)`,
          border: `${r(1)} solid ${alpha(color.accent, 0.2)}`,
          borderRadius: 3
        }}
      >
        <Typography 
          variant="h6"
          sx={{
            color: color.evergreen,
            fontWeight: 600,
            fontSize: '1.05rem',
            mb: 1.5
          }}
        >
          Add Medication
        </Typography>
        
        <Autocomplete
          options={suggestions}
          getOptionLabel={(option) => option.full_name || option.display_name || option.name || ''}
          value={null}
          inputValue={searchQuery}
          onInputChange={handleSearchChange}
          onChange={handleMedicationSelect}
          loading={loading}
          loadingText="Searching medications..."
          filterOptions={(x) => x} // Don't filter, use API results
          renderInput={(params) => (
            <TextField
              {...params}
              ref={searchInputRef}
              placeholder="Search by brand or generic name"
              variant="outlined"
              fullWidth
              onFocus={(e) => {
                // Keep input visible when keyboard opens on mobile
                try {
                  e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } catch {}
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'background.paper',
                  transition: 'all 200ms ease',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(color.accent, 0.5),
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'background.paper',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: color.accent,
                      borderWidth: 2,
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: color.evergreen,
                  }
                }
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <SearchIcon sx={{ 
                    color: searchQuery ? color.accent : 'text.secondary', 
                    mr: 1,
                    transition: 'color 200ms ease'
                  }} />
                ),
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {loading && (
                      <CircularProgress 
                        color="inherit" 
                        size={20} 
                        sx={{ mr: 1 }} 
                        aria-label="Searching medications"
                      />
                    )}
                    {params.InputProps.endAdornment}
                  </Box>
                ),
              }}
            />
          )}
          renderOption={(liProps, option) => {
            const formattedName = {
              displayName: option.display_name || option.name || '',
              secondaryName: option.is_brand ? option.generic_name : null,
              fullName: option.full_name || ''
            };
            return (
                <Box 
                  component="li" 
                  key={liProps.id}
                  {...{...liProps, key: undefined}}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: alpha(color.accent, 0.08),
                    }
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formattedName.displayName}
                      {formattedName.secondaryName && (
                        <Typography 
                          component="span" 
                          variant="body2" 
                          sx={{ 
                            color: color.inkSoft,
                            fontWeight: 500,
                            ml: 1,
                            fontSize: '0.9rem'
                          }}
                        >
                          ({formattedName.secondaryName})
                        </Typography>
                      )}
                    </Typography>
                    {option.strengths && option.strengths.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {option.strengths.length} strength{option.strengths.length > 1 ? 's' : ''} available
                      </Typography>
                    )}
                  </Box>
                </Box>
             );
           }}
        />
        
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 1.5,
              borderRadius: 3,
              '& .MuiAlert-message': {
                fontSize: '0.9rem',
                fontWeight: 500,
              },
              '& .MuiAlert-icon': {
                fontSize: '1.2rem',
              }
            }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        {/* Success message with fade animation */}
        <Collapse in={!!success}>
          <Fade in={showSuccess} timeout={300}>
            <Alert 
              severity="success" 
              sx={{ 
                mt: 1.5,
                borderRadius: 3,
                backgroundColor: alpha(color.accent, 0.1),
                border: `${r(1)} solid ${alpha(color.accent, 0.3)}`,
                '& .MuiAlert-message': {
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: color.evergreen,
                },
                '& .MuiAlert-icon': {
                  color: color.accent,
                }
              }}
              onClose={() => {
                setShowSuccess(false);
                setTimeout(() => setSuccess(''), 300);
              }}
            >
              {success}
            </Alert>
          </Fade>
        </Collapse>
      </Paper>

      {/* Add Medication Form */}
      {showAddForm && selectedMedication && (
        <Paper 
          elevation={2} 
          ref={addFormRef}
          sx={{ 
            p: 3.5, 
            mb: 3, 
            border: `${r(2)} solid ${color.accent}`,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(color.accent, 0.08)} 0%, ${alpha(color.evergreen, 0.03)} 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <MedicationIcon sx={{ color: color.accent, fontSize: '1.5rem', mr: 1.5 }} />
            <Box>
              <Typography 
                variant="h6"
                sx={{
                  color: color.evergreen,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  mb: 0.5
                }}
              >
                Adding: {selectedMedication.full_name || selectedMedication.display_name || selectedMedication.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                 Please select the dosage for this medication 
              </Typography>
            </Box>
          </Box>
          
          {/* Improved form layout with better alignment */}
          <Grid container spacing={3}>
            {/* Strength Selection */}
            {selectedMedication.strengths && selectedMedication.strengths.length > 0 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel 
                    id="strength-select-label"
                    sx={{ fontSize: '0.95rem' }} // Slightly smaller label
                  >
                    Dosage Strength *
                  </InputLabel>
                  <Select
                    labelId="strength-select-label"
                    value={selectedStrength}
                    onChange={(e) => setSelectedStrength(e.target.value)}
                    label="Dosage Strength *"
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: '0.95rem', // Smaller dropdown text
                      },
                      '& .MuiOutlinedInput-input': {
                        fontSize: '0.95rem',
                      }
                    }}
                  >
                    {selectedMedication.strengths.map((strength, index) => (
                      <MenuItem 
                        key={index} 
                        value={index.toString()}
                        sx={{ fontSize: '0.95rem' }} // Smaller menu items
                      >
                        {strength}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Frequency Selection (removed per requirement) */}
            
            {/* Action Buttons */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCancelAdd}
                  sx={{
                    borderColor: alpha(color.accent, 0.5),
                    color: color.evergreen,
                    '&:hover': {
                      borderColor: color.accent,
                      backgroundColor: alpha(color.accent, 0.08),
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleAddMedication}
                  disabled={
                    loading ||
                    (selectedMedication.strengths?.length > 0 && !selectedStrength)
                  }
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                  sx={{
                    backgroundColor: 'primaryCta.main',
                    fontSize: '0.8rem',
                    '&:hover': {
                      backgroundColor: 'primaryCta.main',
                      opacity: 0.9,
                    }
                  }}
                >
                  {loading ? 'Adding...' : 'Add Medication'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* List of User's Medications */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ color: color.evergreen, fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
          <LocalPharmacyIcon sx={{ mr: 1, color: color.accent }} />
          Current Medications ({medications.length})
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          This information is used to screen for potential interactions and fine tune your recommendations
        </Typography>
        {medications.length > 0 ? (
          <Stack spacing={2.5}>
            {medications.map((medication, index) => {
              const formattedName = {
                displayName: medication.name.display,
                secondaryName: medication.is_brand ? medication.name.generic : null,
                fullName: medication.raw?.rxnorm_name
              };
              const strengthDisplay = medication.dosage?.strength
                ? `${medication.dosage.strength} ${medication.dosage.unit || ''}`.trim()
                : medication.raw?.strength_string || 'Not specified';

              return (
                <MedicationCard
                  key={medication.id || index}
                  medication={medication}
                  formattedName={formattedName}
                  strengthDisplay={strengthDisplay}
                  onUpdate={handleUpdateMedication}
                  onRemove={() => handleRemoveMedication(medication.id)}
                />
              );
            })}
          </Stack>
        ) : (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              background: `linear-gradient(135deg, ${alpha(color.accent, 0.05)} 0%, ${alpha(color.evergreen, 0.02)} 100%)`,
              border: `${r(1)} solid ${alpha(color.accent, 0.2)}`,
              borderRadius: 3
            }}
          >
            <MedicationIcon sx={{ fontSize: 48, color: color.accent, mb: 2, opacity: 0.6 }} />
            <Typography variant="h6" sx={{ color: color.evergreen, fontWeight: 600, mb: 1 }}>
              No medication added
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To get started, use the search bar above
            </Typography>
          </Paper>
        )}
      </Box>
    </>
  );
}

// Separate MedicationCard component with editing capability
const MedicationCard = ({ medication, formattedName, strengthDisplay, onUpdate, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({
    frequency: medication.frequency
  });

  const handleEdit = (field) => {
    if (field === 'frequency') {
      setEditingField(field);
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    const updatedMedication = {
      ...medication,
      frequency: tempValues.frequency
    };
    onUpdate(updatedMedication);
    setIsEditing(false);
    setEditingField(null);
  };

  const handleCancel = () => {
    setTempValues({
      frequency: medication.frequency
    });
    setIsEditing(false);
    setEditingField(null);
  };

  const handleFrequencyChange = (e) => {
    const selectedFreq = FREQUENCY_OPTIONS.find(f => f.code === e.target.value);
    if (selectedFreq) {
      setTempValues(prev => ({
        ...prev,
        frequency: {
          code: selectedFreq.code,
          text: selectedFreq.text
        }
      }));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `${r(1)} solid ${color.line}`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: `0 ${r(4)} ${r(20)} ${alpha(color.accent, 0.15)}`,
          borderColor: alpha(color.accent, 0.3),
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          {/* Medication Name */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
              <Typography 
                variant="h6" 
                sx={{
                  color: color.evergreen,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                }}
              >
                {formattedName.displayName}
                {formattedName.secondaryName && (
                  <Typography 
                    component="span" 
                    variant="body2" 
                    sx={{ 
                      color: color.inkSoft,
                      fontWeight: 500,
                      ml: 1,
                      fontSize: '0.9rem'
                    }}
                  >
                    ({formattedName.secondaryName})
                  </Typography>
                )}
              </Typography>
            </Box>
            
            {formattedName.fullName && formattedName.fullName !== formattedName.displayName && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary', 
                  fontSize: '0.85rem',
                  fontStyle: 'italic',
                }}
              >
                {formattedName.fullName}
              </Typography>
            )}
          </Box>

          {/* Compact Medication Details with edit capability */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: r(0.5),
                  fontSize: '0.7rem'
                }}
              >
                Strength:
              </Typography>
              <Chip 
                label={strengthDisplay}
                size="small"
                sx={{
                  backgroundColor: alpha(color.accent, 0.15),
                  color: color.evergreen,
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: r(0.5),
                  fontSize: '0.7rem'
                }}
              >
                Frequency:
              </Typography>
              {isEditing && editingField === 'frequency' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={tempValues.frequency?.code || ''}
                      onChange={handleFrequencyChange}
                      displayEmpty
                      sx={{ height: 24, fontSize: '0.75rem' }}
                    >
                      {FREQUENCY_OPTIONS.map((freq) => (
                        <MenuItem key={freq.code} value={freq.code}>
                          {freq.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={handleSave} sx={{ color: color.accent }}>
                    <CheckIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                  {/* Neutral, not destructive: this dismisses the inline editor
                      next to the accent Save — it is the counterpart of the
                      tick, not a delete control (DESIGN_DOC §10). */}
                  <IconButton size="small" onClick={handleCancel} sx={{ color: color.inkSoft }}>
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip 
                    label={medication.frequency?.text || 'Not specified'}
                    size="small"
                    sx={{
                      backgroundColor: alpha(color.evergreen, 0.1),
                      color: color.evergreen,
                      fontWeight: 500,
                      height: 24,
                      fontSize: '0.75rem',
                    }}
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit('frequency')}
                    sx={{ 
                      color: color.accent,
                      '&:hover': { backgroundColor: alpha(color.accent, 0.1) }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            {medication.dosage.form && medication.dosage.form !== 'N/A' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: r(0.5),
                    fontSize: '0.7rem'
                  }}
                >
                  Form:
                </Typography>
                <Chip 
                  label={medication.dosage.form}
                  size="small"
                  sx={{
                    backgroundColor: alpha(color.evergreen, 0.1),
                    color: color.evergreen,
                    fontWeight: 500,
                    height: 24,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Remove Button */}
        <IconButton 
          onClick={onRemove}
          sx={{
            color: color.destructive,
            ml: 2,
            '&:hover': {
              backgroundColor: alpha(color.destructive, 0.08),
            }
          }}
          aria-label="Remove medication"
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default MedicationList; 